import { getDisplayPhoto } from "../stores/userDisplayUtils";
import { WEBSOCKET_STATE } from "../websocket/webSocketState";
import { formatMessageTime } from "./formatRoomTime";
/** 날짜 구분 라벨: "2026년 3월 17일" 형식 (오늘/어제 없이 항상 전체 날짜) */
export function formatDateLabel(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** `<img>`로 인라인 미리보기 불가능해 파일 첨부로 돌려야 하는 이미지 타입 */
const NON_PREVIEWABLE_IMAGE_TYPES = new Set([
  "image/svg+xml",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);
const isPreviewableImage = (a) =>
  a.contentType?.startsWith("image/") && !NON_PREVIEWABLE_IMAGE_TYPES.has(a.contentType);

export function mapServerMessageToUI(msg, currentUserId) {
  if (!msg) return null;
  const isMy = msg.senderId === currentUserId;
  const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
  const imageAtts = attachments.filter(isPreviewableImage);
  const nonImageAtts = attachments.filter((a) => !isPreviewableImage(a));
  const imagePairs = imageAtts
    .map((a) => ({ url: a.url, thumbnailUrl: a.thumbnailUrl ?? null, name: a.name ?? null }))
    .filter((p) => Boolean(p.url));
  const imageUrls = imagePairs.map((p) => p.url);
  /** thumbnailUrl이 있으면 그리드에서 사용, 없으면 원본 url 폴백 */
  const imageThumbnailUrls = imagePairs.map((p) => p.thumbnailUrl || p.url);
  /** imageUrls와 동일 순서 — 다운로드·미리보기 시 원본명 */
  const imageFileNames = imagePairs.map((p) => p.name);
  const fileAttachments = nonImageAtts.map((a) => ({
    fileName: a.name ?? null,
    fileExtension: a.name?.includes(".") ? (a.name.split(".").pop() ?? null) : null,
    fileUrl: a.url ?? null,
  }));
  const firstFile = fileAttachments[0] ?? null;
  const c = msg.content;
  const content =
    c && String(c.contentId ?? "").trim() !== "" && String(c.contentType ?? "").trim() !== ""
      ? { contentId: c.contentId, contentType: c.contentType }
      : null;
  return {
    id: msg.messageId,
    roomId: msg.roomId ?? null,
    messageSeq: typeof msg.messageSeq === "number" ? msg.messageSeq : null,
    body: msg.text || "",
    time: formatMessageTime(msg.createdAt),
    type: isMy ? "my" : "opponent",
    senderName: msg.senderName || (!isMy ? msg.senderId : ""),
    avatarUrl: !isMy ? (getDisplayPhoto(msg.senderId) ?? null) : null,
    dateLabel: formatDateLabel(msg.createdAt),
    /** 단일 이미지·스토리 호환 — 다중이면 null, ChatMessage는 imageUrls 우선 */
    imageUrl: imageUrls.length === 1 ? imageUrls[0] : null,
    imageUrls: imageUrls.length > 0 ? imageUrls : null,
    imageThumbnailUrls: imageUrls.length > 0 ? imageThumbnailUrls : null,
    imageFileNames: imageUrls.length > 0 ? imageFileNames : null,
    /** 비이미지 첨부 여러 개 → 각각 별도 파일 카드 */
    fileAttachments: fileAttachments.length > 0 ? fileAttachments : null,
    /** 하위 호환·목록 등 */
    fileName: firstFile?.fileName ?? null,
    fileExtension: firstFile?.fileExtension ?? null,
    fileUrl: firstFile?.fileUrl ?? null,
    /** WS content (매물 등 공유 콘텐츠) — UI에서 이미지·파일보다 먼저 표시 */
    content,
    status: WEBSOCKET_STATE.OK,
  };
}

export function mapHistoryMessagesToUI(messages, currentUserId, roomIdFallback = null) {
  if (!Array.isArray(messages)) return [];
  return messages
    .map((msg) => {
      const row = mapServerMessageToUI(msg, currentUserId);
      if (!row) return null;
      if (!row.roomId && roomIdFallback) return { ...row, roomId: roomIdFallback };
      return row;
    })
    .filter(Boolean);
}

/**
 * 재연결 직후 최신 히스토리 페이지만 받았을 때 — 목록 전체를 갈아끼우지 않고
 * 기존 메시지 + 서버에서 온 최근 N개를 id 기준으로 합치고 messageSeq로 정렬.
 * PENDING/FAILED(낙관적 전송) 꼬리는 유지.
 */
export function mergeReconnectMessagesFromHistory(prev, mapped) {
  if (!Array.isArray(prev) || prev.length === 0) {
    return [...mapped];
  }
  const pendingTail = prev.filter(
    (m) =>
      (m.status === WEBSOCKET_STATE.PENDING || m.status === WEBSOCKET_STATE.FAILED) && m.id != null
  );
  const optimisticIds = new Set(pendingTail.map((m) => m.id));
  const byId = new Map();
  for (const m of prev) {
    if (m.id == null) continue;
    if (optimisticIds.has(m.id)) continue;
    if (m.status === WEBSOCKET_STATE.PENDING || m.status === WEBSOCKET_STATE.FAILED) continue;
    byId.set(m.id, m);
  }
  for (const m of mapped) {
    if (m.id) byId.set(m.id, m);
  }
  const sorted = Array.from(byId.values()).sort((a, b) => {
    const sa = typeof a.messageSeq === "number" ? a.messageSeq : -1;
    const sb = typeof b.messageSeq === "number" ? b.messageSeq : -1;
    if (sa !== sb) return sa - sb;
    return String(a.id).localeCompare(String(b.id));
  });
  return [...sorted, ...pendingTail];
}

export function getCurrentUserId() {
  return typeof window !== "undefined" ? window.loggedInUser?.uuid : null;
}

export function getPreviewTextFromLastMessage(lastMessage) {
  if (!lastMessage) return "";
  const text = lastMessage.text || "";
  const types = lastMessage.messageType || [];
  const attachments = Array.isArray(lastMessage.attachments) ? lastMessage.attachments : [];
  const hasContent =
    lastMessage.content && (lastMessage.content.contentId || lastMessage.content.contentType);
  if (text) return text;
  if (attachments.length > 0) {
    const imageAtts = attachments.filter(
      (a) => a.contentType?.startsWith("image/") && a.contentType !== "image/svg+xml"
    );
    const nonImageAtts = attachments.filter(
      (a) => !a.contentType?.startsWith("image/") || a.contentType === "image/svg+xml"
    );
    const parts = [];
    if (imageAtts.length > 0)
      parts.push(imageAtts.length === 1 ? "(이미지)" : `(이미지 ${imageAtts.length})`);
    if (nonImageAtts.length > 0)
      parts.push(nonImageAtts.length === 1 ? "(파일)" : `(파일 ${nonImageAtts.length})`);
    if (parts.length > 0) return parts.join(", ");
  }
  // attachments 정보 없을 때 messageType 폴백
  if (Array.isArray(types) && types.length > 0) {
    const normalized = types.map((t) => String(t).toUpperCase());
    if (normalized.some((t) => t === "IMAGE")) return "(이미지)";
    if (normalized.some((t) => t === "VIDEO")) return "(동영상)";
    if (normalized.some((t) => t === "FILE")) return "(파일)";
  }
  if (hasContent) return "[컨텐츠]";
  return "";
}
